import type { TAuthConfig, TRefreshTokenExpiredEvent } from 'react-oauth2-code-pkce';

type BuildEnv = 'er' | 'wa' | 'wf' | 'all' | undefined;

type CreateAuthConfigOptions = {
  onRefreshTokenExpire?: (event: TRefreshTokenExpiredEvent) => void;
};

const env = process.env.REACT_APP_BUILD_NAME as BuildEnv;
const DEFAULT_CALLBACK_PATH = '/oauth/callback';

export type AuthEnvKey =
  | 'VITE_ER_AUTH_CLIENT_ID'
  | 'VITE_ER_AUTH_AUTHORIZATION_ENDPOINT'
  | 'VITE_ER_AUTH_TOKEN_ENDPOINT'
  | 'VITE_ER_AUTH_REDIRECT_URI'
  | 'VITE_ER_AUTH_SCOPE';

const AUTH_ENV_KEYS: AuthEnvKey[] = [
  'VITE_ER_AUTH_CLIENT_ID',
  'VITE_ER_AUTH_AUTHORIZATION_ENDPOINT',
  'VITE_ER_AUTH_TOKEN_ENDPOINT',
  'VITE_ER_AUTH_REDIRECT_URI',
  'VITE_ER_AUTH_SCOPE',
];

function getEnvValue(key: AuthEnvKey) {
  return process.env[`REACT_APP_${key}`]?.trim() ?? process.env[key]?.trim() ?? '';
}

export function isErisAuthBuild() {
  return env === 'er' || env === 'all';
}

export function getMissingAuthConfigKeys() {
  if (!isErisAuthBuild()) return [];
  return AUTH_ENV_KEYS.filter((key) => !getEnvValue(key));
}

export function getAuthRedirectPathname() {
  const redirectUri = getEnvValue('VITE_ER_AUTH_REDIRECT_URI');
  if (!redirectUri) return DEFAULT_CALLBACK_PATH;

  try {
    return new URL(redirectUri, window.location.origin).pathname || DEFAULT_CALLBACK_PATH;
  } catch {
    return DEFAULT_CALLBACK_PATH;
  }
}

export function getErisLoginPath() {
  return env === 'er' ? '/login' : '/er/login';
}

export function getAuthorizeUrlPreview(state?: string) {
  const missingKeys = getMissingAuthConfigKeys();
  if (missingKeys.length > 0) return null;

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: getEnvValue('VITE_ER_AUTH_CLIENT_ID'),
    code_challenge: '<generated-by-library>',
    code_challenge_method: 'S256',
    scope: getEnvValue('VITE_ER_AUTH_SCOPE'),
    redirect_uri: getEnvValue('VITE_ER_AUTH_REDIRECT_URI'),
  });

  if (state) {
    params.append('state', state);
  }

  return `${getEnvValue('VITE_ER_AUTH_AUTHORIZATION_ENDPOINT')}?${params.toString()}`;
}

export function createAuthConfig(options: CreateAuthConfigOptions = {}): TAuthConfig | null {
  if (!isErisAuthBuild()) return null;

  const missingKeys = getMissingAuthConfigKeys();
  if (missingKeys.length > 0) return null;

  const authConfig: TAuthConfig = {
    clientId: getEnvValue('VITE_ER_AUTH_CLIENT_ID'),
    authorizationEndpoint: getEnvValue('VITE_ER_AUTH_AUTHORIZATION_ENDPOINT'),
    tokenEndpoint: getEnvValue('VITE_ER_AUTH_TOKEN_ENDPOINT'),
    redirectUri: getEnvValue('VITE_ER_AUTH_REDIRECT_URI'),
    scope: getEnvValue('VITE_ER_AUTH_SCOPE'),
    autoLogin: false,
    clearURL: true,
    decodeToken: false,
    loginMethod: 'redirect',
    storage: 'session',
    storageKeyPrefix: 'ER_OAUTH_',
  };

  if (options.onRefreshTokenExpire) {
    authConfig.onRefreshTokenExpire = options.onRefreshTokenExpire;
  }

  return authConfig;
}
